/**
 * @author      sam@zumper.com (Sam Sherman)
 * @copyright   Copyright (c) 2018, Zumper
 * @description trackEvent function for google analytics
 */

import { logger } from '../../logger'
import { ON_BROWSER } from '../env'

export interface GADataLayerEvent {
  event: 'GAEvent'
  eventCategory: string
  eventAction: string
  eventLabel: string | number | null | undefined
  eventValue: string | number | undefined
  nonInteraction: boolean | undefined
}

function hasDataLayer<W extends Window>(
  window: W
): window is W & { dataLayer: { push: (event: GADataLayerEvent) => void } } {
  // @ts-expect-error
  return !!window.dataLayer
}

function hasGa<W extends Window>(
  window: W
): window is W & {
  ga: (
    action: string,
    type: string,
    eventCategory: string,
    eventAction: string,
    eventLabel?: string | number | null,
    eventValue?: string | number,
    fields?: { nonInteraction?: boolean } & Record<string, unknown>
  ) => void
} {
  // @ts-expect-error
  return !!window.ga
}

export const trackGAEvent = (
  category: string,
  action: string,
  label?: string | number | null,
  value?: string | number,
  fields?: { nonInteraction?: boolean } & Record<string, unknown>
) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    if (!category || !action) {
      logger.warn('Could not track event:', category, action)
    } else {
      if (hasDataLayer(window)) {
        logger.debug('Tracking Event gtm:', '[', category, '|', action, '|', label, '|', value, ']')
        // Google Tag Manager
        // note: GA dataLayer does not take a fields variable.
        //       However, it does take nonInteraction {Boolean} so we pass it as a custom field
        window.dataLayer.push({
          event: 'GAEvent',
          eventCategory: category,
          eventAction: action,
          eventLabel: label,
          eventValue: value,
          nonInteraction: fields?.nonInteraction,
        })
      } else if (hasGa(window)) {
        logger.debug('Tracking Event ga:', '[', category, '|', action, '|', label, '|', value, ']')
        window.ga('send', 'event', category, action, label, value, fields)
      }
    }
  }
}
